import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from './Toast';

/**
 * Public Events page — shows upcoming and past community events.
 */
export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/events', { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        setEvents(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const now = new Date();
  const upcoming = events.filter(e => new Date(e.date) >= now);
  const past = events.filter(e => new Date(e.date) < now).reverse();

  return (
    <section className="px-4 md:px-6 py-10 md:py-16">
      <div className="max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
            <span className="text-[10px] font-mono text-white/30 tracking-[0.3em] uppercase">Schedule</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-3 text-center">
            Community Events
          </h2>
          <p className="text-white/45 text-sm text-center max-w-md mx-auto leading-relaxed">
            Tournaments, game nights, and community gatherings
          </p>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="rounded-lg border border-white/[0.05] p-4 md:p-5" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-5 w-48" />
                    <div className="skeleton h-3 w-64" />
                    <div className="skeleton h-3 w-32" />
                  </div>
                  <div className="skeleton h-7 w-14 rounded-md" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
              <svg className="w-7 h-7 text-white/15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
            </div>
            <p className="text-white/30 text-sm font-mono mb-2">No events scheduled yet</p>
            <p className="text-white/15 text-xs font-mono">Check back soon or join the Discord for announcements</p>
          </div>
        ) : (
          <>
            {/* Upcoming Events */}
            {upcoming.length > 0 && (
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <h3 className="text-sm font-bold text-white/50 tracking-[0.15em] uppercase">Upcoming</h3>
                </div>
                <div className="space-y-3">
                  {upcoming.map(event => (
                    <EventCard key={event.id} event={event} isUpcoming />
                  ))}
                </div>
              </div>
            )}

            {/* Past Events */}
            {past.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-sm font-semibold text-white/30 tracking-[0.15em] uppercase">Past Events</h3>
                </div>
                <div className="space-y-3 opacity-60">
                  {past.map(event => (
                    <EventCard key={event.id} event={event} isUpcoming={false} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function EventCard({ event, isUpcoming }) {
  const [expanded, setExpanded] = useState(false);
  const toast = useToast();

  // RSVP state from localStorage
  const rsvpKey = `tcc_rsvp_${event.id}`;
  const [hasRsvp, setHasRsvp] = useState(() => localStorage.getItem(rsvpKey) === 'true');
  const [rsvpCount, setRsvpCount] = useState(event.rsvpCount || 0);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  const toggleRsvp = useCallback(async (e) => {
    e.stopPropagation();
    if (rsvpLoading) return;
    setRsvpLoading(true);
    const action = hasRsvp ? 'leave' : 'join';
    try {
      const res = await fetch(`/api/events/${event.id}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (data.success) {
        setRsvpCount(data.rsvpCount);
        setHasRsvp(!hasRsvp);
        localStorage.setItem(rsvpKey, !hasRsvp ? 'true' : 'false');
        toast.success(hasRsvp ? 'Left the event' : `🎮 You're in! See you there`);
      }
    } catch {
      toast.error('Failed to update RSVP');
    }
    setRsvpLoading(false);
  }, [hasRsvp, rsvpLoading, event.id, rsvpKey, toast]);

  const eventDate = new Date(event.date + (event.time ? `T${event.time}` : ''));
  const dateStr = eventDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const timeStr = event.time ? eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) : '';

  // Countdown for upcoming events
  const now = new Date();
  const diff = eventDate - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  let countdown = '';
  if (isUpcoming && diff > 0) {
    if (days > 0) countdown = `${days}d ${hours}h`;
    else if (hours > 0) countdown = `${hours}h`;
    else countdown = 'Starting soon';
  }

  return (
    <div className={`rounded-lg border transition-all duration-300 ${
      isUpcoming
        ? 'bg-white/[0.03] border-white/[0.08] hover:border-blue-500/25 hover:bg-white/[0.04]'
        : 'bg-white/[0.015] border-white/[0.04]'
    }`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 md:p-5"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <h3 className={`text-base font-semibold truncate ${isUpcoming ? 'text-white' : 'text-white/60'}`}>
                {event.title}
              </h3>
              {event.game && (
                <span className="flex-shrink-0 px-2 py-0.5 rounded text-[9px] font-bold font-mono bg-blue-500/10 border border-blue-500/20 text-blue-400/70">
                  {event.game}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-mono text-white/40 inline-flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                {dateStr}
              </span>
              {timeStr && (
                <span className="text-xs font-mono text-white/30">
                  {timeStr}
                </span>
              )}
              <span className="text-xs font-mono text-white/20">
                Hosted by {event.host}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* RSVP button */}
            {isUpcoming && (
              <button
                onClick={toggleRsvp}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] font-bold font-mono transition-all duration-200 cursor-pointer ${
                  hasRsvp
                    ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 hover:bg-red-500/10 hover:border-red-500/20 hover:text-red-400/70'
                    : 'bg-white/[0.04] border border-white/[0.08] text-white/40 hover:border-emerald-500/25 hover:text-emerald-400'
                }`}
                title={hasRsvp ? 'Click to leave' : 'Click to RSVP'}
              >
                <span>{hasRsvp ? '✓' : '🙋'}</span>
                <span>{hasRsvp ? "I'm In" : 'Join'}</span>
                {rsvpCount > 0 && (
                  <span className={`ml-0.5 px-1 py-px rounded text-[9px] ${
                    hasRsvp ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/[0.06] text-white/30'
                  }`}>
                    {rsvpCount}
                  </span>
                )}
              </button>
            )}

            {countdown && (
              <span className="px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-bold font-mono text-emerald-400">
                {countdown}
              </span>
            )}
            <svg className={`w-4 h-4 text-white/20 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 md:px-5 pb-4 md:pb-5 border-t border-white/[0.04] pt-4 space-y-3">
          {event.description && (
            <p className="text-sm text-white/50 leading-relaxed">{event.description}</p>
          )}

          <div className="flex flex-wrap gap-4 text-xs font-mono text-white/30">
            {event.maxPlayers > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
                Max {event.maxPlayers} players
              </span>
            )}
          </div>

          {event.rules && (
            <div className="mt-3 p-3 rounded-md bg-white/[0.02] border border-white/[0.04]">
              <p className="text-[10px] font-mono text-white/25 uppercase tracking-wider mb-1.5">Rules</p>
              <p className="text-xs text-white/40 whitespace-pre-line">{event.rules}</p>
            </div>
          )}

          {event.discordLink && isUpcoming && (
            <a
              href={event.discordLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#5865F2]/15 border border-[#5865F2]/25 text-[#5865F2] text-xs font-mono hover:bg-[#5865F2]/25 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.492c-1.53-.742-3.17-1.297-4.885-1.515a.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.566 18.566 0 00-5.487 0 12.36 12.36 0 00-.617-1.25.077.077 0 00-.079-.037c-1.714.218-3.354.773-4.885 1.515a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z" /></svg>
              Sign Up on Discord
            </a>
          )}
        </div>
      )}
    </div>
  );
}
